<template>
  <div class="card p-3 shadow-sm">
    <!-- Toast -->
    <div v-if="mostrarToast" class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1055;">
      <div class="toast show align-items-center text-white" :class="toastColor">
        <div class="d-flex">
          <div class="toast-body">
            <i :class="iconoToast" class="me-2"></i> {{ alerta.mensaje }}
          </div>
          <button type="button" class="btn-close btn-close-white me-2 m-auto" @click="cerrarToast"></button>
        </div>
      </div>
    </div>

    <div class="container-fluid my-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h4 class="fw-semibold text-primary mb-0">💳 Cuentas por Mesa</h4>
      </div>

      <!-- 🔍 Buscador -->
      <div class="row g-2 mb-4 align-items-end">
        <div class="col-12 col-md-6 col-lg-4">
          <label class="form-label small text-muted mb-1">Buscar mesa</label>
          <input
            type="text"
            class="form-control form-control-sm"
            placeholder="Ejemplo: 45N"
            v-model="busqueda"
            @input="filtrarMesas"
          />
        </div>
        <div class="col-auto">
          <button class="btn btn-outline-secondary btn-sm" @click="toggleTodasMesas">
            {{ mesasAbiertas.length === mesasFiltradas.length ? '▲ Colapsar todo' : '▼ Expandir todo' }}
          </button>
        </div>
      </div>

      <!-- Barra de unificación (visible cuando hay seleccionadas) -->
      <div v-if="cuentasSeleccionadas.length > 0" class="alert alert-warning d-flex justify-content-between align-items-center mb-3">
        <span>
          <strong>{{ cuentasSeleccionadas.length }}</strong> cuenta(s) seleccionada(s) para unificar.
          Total: <strong>Q{{ totalSeleccionado.toFixed(2) }}</strong>
        </span>
        <div class="d-flex gap-2">
          <button class="btn btn-sm btn-secondary" @click="limpiarSeleccion">Cancelar</button>
          <button class="btn btn-sm btn-warning" @click="abrirModalUnificar" :disabled="cuentasSeleccionadas.length < 2">
            🔗 Unificar seleccionadas
          </button>
        </div>
      </div>

      <!-- 📋 Listado -->
      <div v-if="mesasFiltradas.length" class="row g-3">
        <div
          v-for="mesa in mesasFiltradas"
          :key="mesa.mesa_numero"
          class="col-12 col-md-6 col-lg-4"
        >
          <div class="mesa-card border rounded shadow-sm h-100 d-flex flex-column">
            <!-- Encabezado mesa -->
            <div
              class="mesa-header d-flex justify-content-between align-items-center px-3 py-2 bg-primary text-white rounded-top"
              role="button"
              @click="toggleMesa(mesa.mesa_numero)"
            >
              <h6 class="mb-0 fw-semibold">Mesa #{{ mesa.mesa_numero }}</h6>
              <i
                :class="mesasAbiertas.includes(mesa.mesa_numero) ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"
                class="fs-6"
              ></i>
            </div>

            <!-- Pedidos y Cuentas -->
            <transition name="fade">
              <div v-if="mesasAbiertas.includes(mesa.mesa_numero)" class="mesa-body p-3 flex-grow-1">
                <div v-for="pedido in mesa.pedidos" :key="pedido.IdPedido" class="mb-3">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="text-secondary fw-semibold mb-0">
                      🧾 Pedido #{{ pedido.IdPedido }}
                      <span
                        class="badge ms-2"
                        :class="pedido.EstadoPedido === 1 ? 'bg-success' : 'bg-warning text-dark'"
                      >
                        {{ pedido.EstadoPedido === 1 ? 'Cerrado' : 'Abierto' }}
                      </span>
                    </h6>
                    <div class="d-flex gap-1">
                      <button
                        v-if="esPedidoLimpiable(pedido)"
                        class="btn btn-sm btn-outline-danger"
                        @click="abrirModalLimpiar(pedido)"
                        :disabled="limpiando === pedido.IdPedido"
                        title="Eliminar pedido vacío (sin monto ni pagos)"
                      >
                        <span v-if="limpiando === pedido.IdPedido" class="spinner-border spinner-border-sm me-1"></span>
                        🧹 Limpiar
                      </button>
                      <button
                        class="btn btn-sm btn-outline-primary"
                        @click="irAImprimirPedido(pedido.IdPedido)"
                        title="Imprimir cuentas del pedido"
                      >
                        🖨️ Imprimir
                      </button>
                    </div>
                  </div>

                  <div
                    v-for="cuenta in pedido.cuentas"
                    :key="cuenta.IdCuentaPedido"
                    class="border rounded p-2 mb-2 small shadow-sm bg-light d-flex justify-content-between align-items-center flex-wrap"
                    :class="{ 'border-warning': estaSeleccionada(cuenta.IdCuentaPedido) }"
                  >
                    <div class="d-flex align-items-start gap-2 me-2">
                      <input
                        v-if="cuenta.Estado === 0"
                        type="checkbox"
                        class="form-check-input mt-1"
                        :checked="estaSeleccionada(cuenta.IdCuentaPedido)"
                        @change="toggleSeleccion(cuenta, mesa.mesa_numero)"
                        title="Seleccionar para unificar"
                      />
                      <div>
                        <strong>{{ cuenta.NombreCuenta }}</strong><br />
                        <span class="text-muted">
                          Total: Q{{ parseFloat(cuenta.Total).toFixed(2) }}
                        </span><br />
                        <span class="text-muted">
                          Pendiente: Q{{ parseFloat(cuenta.SaldoPendienteCuenta).toFixed(2) }}
                        </span>
                        <template v-if="parseFloat(cuenta.SaldoDescuento || 0) > 0.01">
                          <br />
                          <span class="text-success">
                            Descuento: Q{{ parseFloat(cuenta.SaldoDescuento).toFixed(2) }}
                          </span>
                        </template>
                      </div>
                    </div>

                    <div class="text-end mt-1">
                      <span
                        class="badge mb-1"
                        :class="estadoBadgeClass(cuenta.Estado)"
                      >
                        {{ estadoLabel(cuenta.Estado) }}
                      </span>
                      <br />
                      <button
                        v-if="$store.state.user?.IdRol !== 8"
                        class="btn btn-sm btn-outline-success mt-1 me-1"
                        @click="irAPagar(cuenta, pedido.IdPedido)"
                        :disabled="cuenta.Estado !== 0 || pedido.EstadoPedido !== 1"
                      >
                        💵 Pagar
                      </button>
                      <button
                        class="btn btn-sm btn-outline-info mt-1"
                        @click="abrirModalCargarHabitacion(cuenta)"
                        :disabled="cuenta.Estado !== 0 || pedido.EstadoPedido !== 1"
                        title="Cargar esta cuenta a una habitación"
                      >
                        🏠 Habitación
                      </button>
                      <button
                        v-if="esCuentaSaldada(cuenta, pedido)"
                        class="btn btn-sm btn-outline-secondary mt-1 ms-1"
                        @click="abrirModalCerrarSaldada(cuenta, pedido)"
                        :disabled="cerrandoSaldada === cuenta.IdCuentaPedido"
                        title="La cuenta ya no debe nada: marcarla como saldada y quitarla de esta lista"
                      >
                        <span v-if="cerrandoSaldada === cuenta.IdCuentaPedido" class="spinner-border spinner-border-sm me-1"></span>
                        🧹 Limpiar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <div v-else class="text-center text-muted fs-6 mt-4">
        No se encontraron cuentas pendientes.
      </div>
    </div>

    <!-- Modal: Unificar Cuentas -->
    <div v-if="mostrarModalUnificar" class="modal-backdrop" @click.self="cerrarModalUnificar">
      <div class="modal-content-custom" style="max-width: 560px;">
        <div class="modal-header-custom">
          <h5>🔗 Unificar Cuentas</h5>
          <button class="btn-close-custom" @click="cerrarModalUnificar">×</button>
        </div>
        <div class="modal-body-custom">
          <p class="mb-2 small text-muted">Seleccione la cuenta <strong>destino</strong> que absorberá a las demás.</p>

          <div class="mb-3">
            <label class="form-label fw-semibold small">Cuenta destino (recibirá el saldo):</label>
            <select v-model="cuentaDestinoId" class="form-select form-select-sm">
              <option :value="null" disabled>Seleccione una cuenta destino</option>
              <option v-for="c in cuentasSeleccionadas" :key="c.IdCuentaPedido" :value="c.IdCuentaPedido">
                {{ c.NombreCuenta }} [Mesa {{ c._mesa }}] — Q{{ parseFloat(c.SaldoPendienteCuenta).toFixed(2) }}
              </option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold small">Cuentas a unificar (origen):</label>
            <ul class="list-group list-group-flush small">
              <li v-for="c in cuentasOrigen" :key="c.IdCuentaPedido" class="list-group-item py-1 px-2">
                {{ c.NombreCuenta }}
                <span class="badge bg-secondary ms-1">Mesa {{ c._mesa }}</span>
                — <strong>Q{{ parseFloat(c.SaldoPendienteCuenta).toFixed(2) }}</strong>
              </li>
            </ul>
          </div>

          <div class="alert alert-warning py-2 small" v-if="cuentaDestinoId">
            El saldo total de <strong>Q{{ totalOrigenUnificacion.toFixed(2) }}</strong>
            se sumará a la cuenta destino.<br/>
            Nuevo saldo destino: <strong>Q{{ nuevoSaldoDestino.toFixed(2) }}</strong>
          </div>
        </div>
        <div class="modal-footer-custom">
          <button class="btn btn-secondary btn-sm" @click="cerrarModalUnificar" :disabled="cargandoUnificacion">Cancelar</button>
          <button
            class="btn btn-warning btn-sm"
            @click="confirmarUnificacion"
            :disabled="!cuentaDestinoId || cargandoUnificacion"
          >
            <span v-if="cargandoUnificacion" class="spinner-border spinner-border-sm me-1"></span>
            🔗 Confirmar Unificación
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Cargar Cuenta a Habitación -->
    <div v-if="mostrarModalHabitacion" class="modal-backdrop" @click="cerrarModalHabitacion">
      <div class="modal-content-custom" @click.stop>
        <div class="modal-header-custom">
          <h5>🏠 Cargar Cuenta a Habitación</h5>
          <button class="btn-close-custom" @click="cerrarModalHabitacion">×</button>
        </div>
        <div class="modal-body-custom">
          <p class="mb-3">
            Cuenta: <strong>{{ cuentaSeleccionada?.NombreCuenta }}</strong><br />
            Total: <strong>Q{{ parseFloat(cuentaSeleccionada?.Total || 0).toFixed(2) }}</strong>
          </p>

          <label class="form-label">Buscar habitación (número, huésped o código):</label>
          <input
            type="text"
            class="form-control mb-3"
            v-model="busquedaHabitacion"
            placeholder="Ej: 101, Juan Pérez, R00123"
            @input="filtrarHabitaciones"
          />

          <div v-if="habitacionesFiltradas.length" class="habitaciones-lista">
            <div
              v-for="hab in habitacionesFiltradas"
              :key="hab.IdReservaHabitacion"
              class="habitacion-item"
              :class="{ 'selected': habitacionSeleccionada === hab.IdReservaHabitacion }"
              @click="habitacionSeleccionada = hab.IdReservaHabitacion"
            >
              <strong>Hab. {{ hab.Numero || 'N/A' }}</strong><br />
              <small>{{ hab.huesped?.Nombre || '' }} {{ hab.huesped?.Apellido || '' }}</small><br />
              <small class="text-muted">Código: {{ hab.CodigoReserva || 'N/A' }}</small>
            </div>
          </div>
          <div v-else class="text-muted small">
            No se encontraron habitaciones ocupadas.
          </div>
        </div>
        <div class="modal-footer-custom">
          <button class="btn btn-secondary btn-sm" @click="cerrarModalHabitacion">Cancelar</button>
          <button
            class="btn btn-primary btn-sm"
            @click="confirmarCargaHabitacion"
            :disabled="!habitacionSeleccionada"
          >
            ✅ Confirmar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Confirmar Limpieza de Pedido Vacío -->
    <div v-if="mostrarModalLimpiar" class="modal-backdrop" @click.self="cerrarModalLimpiar">
      <div class="modal-content-custom" style="max-width: 480px;">
        <div class="modal-header-custom">
          <h5>🧹 Eliminar Pedido</h5>
          <button class="btn-close-custom" @click="cerrarModalLimpiar">×</button>
        </div>
        <div class="modal-body-custom">
          <p class="mb-0">
            ¿Eliminar el pedido #{{ pedidoALimpiar?.IdPedido }}? Esta acción no se puede deshacer.
          </p>
        </div>
        <div class="modal-footer-custom">
          <button class="btn btn-secondary btn-sm" @click="cerrarModalLimpiar" :disabled="limpiando !== null">Cancelar</button>
          <button
            class="btn btn-danger btn-sm"
            @click="confirmarLimpiarPedido"
            :disabled="limpiando !== null"
          >
            <span v-if="limpiando !== null" class="spinner-border spinner-border-sm me-1"></span>
            🧹 Eliminar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Confirmar Cierre de Cuenta Saldada -->
    <div v-if="mostrarModalSaldada" class="modal-backdrop" @click.self="cerrarModalSaldada">
      <div class="modal-content-custom" style="max-width: 480px;">
        <div class="modal-header-custom">
          <h5>🧹 Limpiar Cuenta Saldada</h5>
          <button class="btn-close-custom" @click="cerrarModalSaldada">×</button>
        </div>
        <div class="modal-body-custom">
          <p class="mb-2">
            La cuenta <strong>{{ cuentaASaldar?.NombreCuenta }}</strong> del pedido
            #{{ cuentaASaldar?._idPedido }} ya no tiene saldo pendiente.
          </p>
          <ul class="small text-muted mb-2">
            <li>Total: Q{{ parseFloat(cuentaASaldar?.Total || 0).toFixed(2) }}</li>
            <li v-if="parseFloat(cuentaASaldar?.SaldoDescuento || 0) > 0.01">
              Cubierto por descuento: Q{{ parseFloat(cuentaASaldar.SaldoDescuento).toFixed(2) }}
            </li>
            <li>Pendiente: Q{{ parseFloat(cuentaASaldar?.SaldoPendienteCuenta || 0).toFixed(2) }}</li>
          </ul>
          <p class="mb-0 small">
            Se marcará como <strong>pagada</strong> y saldrá de esta lista.
            El consumo, el descuento y los pagos se conservan.
          </p>
        </div>
        <div class="modal-footer-custom">
          <button class="btn btn-secondary btn-sm" @click="cerrarModalSaldada" :disabled="cerrandoSaldada !== null">Cancelar</button>
          <button
            class="btn btn-primary btn-sm"
            @click="confirmarCerrarSaldada"
            :disabled="cerrandoSaldada !== null"
          >
            <span v-if="cerrandoSaldada !== null" class="spinner-border spinner-border-sm me-1"></span>
            🧹 Limpiar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { API_URL } from '@/config/config'

export default {
  name: 'CuentasMesas',
  data() {
    return {
      mesas: [],
      mesasFiltradas: [],
      busqueda: '',
      intervalo: null,
      actualizando: false,
      mostrarModalHabitacion: false,
      cuentaSeleccionada: null,
      habitaciones: [],
      habitacionesFiltradas: [],
      habitacionSeleccionada: null,
      busquedaHabitacion: '',
      mostrarToast: false,
      alerta: { mensaje: '', tipo: 'success' },
      // Unificación
      cuentasSeleccionadas: [],
      mostrarModalUnificar: false,
      cuentaDestinoId: null,
      cargandoUnificacion: false,
      // Mesas (múltiples abiertas simultáneamente)
      mesasAbiertas: [],
      // Limpiar pedido vacío
      mostrarModalLimpiar: false,
      pedidoALimpiar: null,
      limpiando: null,
      // Limpiar cuenta saldada (pendiente 0 pero con monto cubierto por descuento/pagos)
      mostrarModalSaldada: false,
      cuentaASaldar: null,
      cerrandoSaldada: null,
    }
  },
  computed: {
    toastColor() {
      return this.alerta.tipo === 'success' ? 'bg-success' : 'bg-danger';
    },
    iconoToast() {
      return this.alerta.tipo === 'success' ? 'bi bi-check-circle-fill' : 'bi bi-x-circle-fill';
    },
    totalSeleccionado() {
      return this.cuentasSeleccionadas.reduce((sum, c) => sum + parseFloat(c.SaldoPendienteCuenta || 0), 0);
    },
    cuentasOrigen() {
      return this.cuentasSeleccionadas.filter(c => c.IdCuentaPedido !== this.cuentaDestinoId);
    },
    totalOrigenUnificacion() {
      return this.cuentasOrigen.reduce((sum, c) => sum + parseFloat(c.SaldoPendienteCuenta || 0), 0);
    },
    nuevoSaldoDestino() {
      const destino = this.cuentasSeleccionadas.find(c => c.IdCuentaPedido === this.cuentaDestinoId);
      return (destino ? parseFloat(destino.SaldoPendienteCuenta || 0) : 0) + this.totalOrigenUnificacion;
    },
  },
  methods: {
    async cargarCuentas() {
      try {
        this.actualizando = true
        const { data } = await axios.get(`${API_URL}/cuentas-mesa`)
        this.mesas = data
        this.filtrarMesas()
      } catch (err) {
        console.error('Error al cargar las cuentas por mesa:', err)
      } finally {
        this.actualizando = false
      }
    },
    filtrarMesas() {
      const texto = this.busqueda.toLowerCase()
      this.mesasFiltradas = !texto
        ? this.mesas
        : this.mesas.filter(m => m.mesa_numero.toLowerCase().includes(texto))
    },
    toggleMesa(numero) {
      const idx = this.mesasAbiertas.indexOf(numero)
      if (idx === -1) {
        this.mesasAbiertas.push(numero)
      } else {
        this.mesasAbiertas.splice(idx, 1)
      }
    },
    toggleTodasMesas() {
      if (this.mesasAbiertas.length === this.mesasFiltradas.length) {
        this.mesasAbiertas = []
      } else {
        this.mesasAbiertas = this.mesasFiltradas.map(m => m.mesa_numero)
      }
    },
    async abrirModalCargarHabitacion(cuenta) {
      this.cuentaSeleccionada = cuenta
      this.mostrarModalHabitacion = true
      this.habitacionSeleccionada = null
      this.busquedaHabitacion = ''
      await this.cargarHabitacionesOcupadas()
    },
    cerrarModalHabitacion() {
      this.mostrarModalHabitacion = false
      this.cuentaSeleccionada = null
      this.habitaciones = []
      this.habitacionesFiltradas = []
      this.habitacionSeleccionada = null
      this.busquedaHabitacion = ''
    },
    async cargarHabitacionesOcupadas() {
      try {
        const { data } = await axios.get(`${API_URL}/habitaciones-ocupadas-restaurante`)
        this.habitaciones = Array.isArray(data) ? data : []
        this.habitacionesFiltradas = this.habitaciones
      } catch (err) {
        console.error('Error al cargar habitaciones ocupadas:', err)
        this.habitaciones = []
        this.habitacionesFiltradas = []
        this.mostrarAlerta('No se pudieron cargar las habitaciones ocupadas', 'danger')
      }
    },
    filtrarHabitaciones() {
      if (!Array.isArray(this.habitaciones)) {
        this.habitacionesFiltradas = []
        return
      }

      const texto = this.busquedaHabitacion.toLowerCase().trim()
      if (!texto) {
        this.habitacionesFiltradas = [...this.habitaciones]
        return
      }

      try {
        this.habitacionesFiltradas = this.habitaciones.filter(h => {
          const numero = (h.Numero || '').toString().toLowerCase()
          const nombre = (h.huesped?.Nombre || '').toLowerCase()
          const apellido = (h.huesped?.Apellido || '').toLowerCase()
          const codigo = (h.CodigoReserva || '').toLowerCase()
          const nombreCompleto = `${nombre} ${apellido}`
          return (
            numero.includes(texto) ||
            nombreCompleto.includes(texto) ||
            codigo.includes(texto)
          )
        })
      } catch (error) {
        console.error('Error filtrando habitaciones:', error)
        this.habitacionesFiltradas = []
      }
    },
    async confirmarCargaHabitacion() {
      if (!this.habitacionSeleccionada || !this.cuentaSeleccionada) return

      try {
        await axios.put(
          `${API_URL}/pagar-cuenta-pedido/${this.cuentaSeleccionada.IdCuentaPedido}/cargar-habitacion`,
          { id_reserva_habitacion: this.habitacionSeleccionada }
        )
        this.mostrarAlerta('Cuenta cargada exitosamente a la habitación', 'success')
        this.cerrarModalHabitacion()
        this.cargarCuentas()
      } catch (err) {
        console.error('Error al cargar cuenta a habitación:', err)
        this.mostrarAlerta('Error al cargar la cuenta. Verifica que la cuenta no esté ya pagada', 'danger')
      }
    },
    irAPagar(cuenta, idPedido) {
      this.$router.push({
        name: 'PagarCuentaPedido',
        params: {
          idCuenta: cuenta.IdCuentaPedido,
          idPedido: idPedido,
        },
      })
    },
    irAImprimirPedido(idPedido) {
      this.$router.push({
        path: `/cuentas-del-pedido/${idPedido}`
      })
    },
    iniciarPolling() {
      // Evita duplicar intervalos
      if (this.intervalo) clearInterval(this.intervalo)

      this.intervalo = setInterval(() => {
        if (!document.hidden) {
          // Solo actualiza si la pestaña está activa
          this.cargarCuentas()
        }
      }, 15000) // cada 15 segundos
    },
    detenerPolling() {
      if (this.intervalo) clearInterval(this.intervalo)
    },
    mostrarAlerta(mensaje, tipo = 'success') {
      this.alerta = { mensaje, tipo }
      this.mostrarToast = true
      setTimeout(() => {
        this.mostrarToast = false
      }, 3500)
    },
    cerrarToast() {
      this.mostrarToast = false
    },
    estadoLabel(estado) {
      const labels = { 0: 'Pendiente', 1: 'En Habitación', 2: 'Pagada', 3: 'Unificada' };
      return labels[estado] ?? 'Desconocido';
    },
    estadoBadgeClass(estado) {
      const classes = { 0: 'bg-danger', 1: 'bg-info', 2: 'bg-success', 3: 'bg-secondary' };
      return classes[estado] ?? 'bg-light text-dark';
    },
    estaSeleccionada(idCuenta) {
      return this.cuentasSeleccionadas.some(c => c.IdCuentaPedido === idCuenta);
    },
    toggleSeleccion(cuenta, mesaNumero) {
      if (this.estaSeleccionada(cuenta.IdCuentaPedido)) {
        this.cuentasSeleccionadas = this.cuentasSeleccionadas.filter(c => c.IdCuentaPedido !== cuenta.IdCuentaPedido);
      } else {
        this.cuentasSeleccionadas.push({ ...cuenta, _mesa: mesaNumero });
      }
    },
    limpiarSeleccion() {
      this.cuentasSeleccionadas = [];
    },
    abrirModalUnificar() {
      this.cuentaDestinoId = null;
      this.mostrarModalUnificar = true;
    },
    cerrarModalUnificar() {
      this.mostrarModalUnificar = false;
      this.cuentaDestinoId = null;
    },
    async confirmarUnificacion() {
      if (!this.cuentaDestinoId || this.cuentasOrigen.length === 0) return;
      try {
        this.cargandoUnificacion = true;
        const idEmpleado = this.$store.state.user?.IdEmpleado;
        await axios.post(`${API_URL}/cuentas-restaurante/unificar`, {
          id_cuenta_destino: this.cuentaDestinoId,
          ids_cuentas_origen: this.cuentasOrigen.map(c => c.IdCuentaPedido),
          id_empleado: idEmpleado,
        });
        this.mostrarAlerta('Cuentas unificadas correctamente', 'success');
        this.cerrarModalUnificar();
        this.limpiarSeleccion();
        await this.cargarCuentas();
      } catch (err) {
        this.mostrarAlerta(err.response?.data?.message || 'Error al unificar cuentas', 'danger');
      } finally {
        this.cargandoUnificacion = false;
      }
    },
    // Cuenta con monto real (Total > 0) cuyo pendiente ya está en 0 porque un
    // cupón y/o los pagos la cubrieron, pero que se quedó en Estado 0 y por eso
    // nunca sale de esta lista. No se borra: se cierra como pagada.
    // El caso Total = 0 lo cubre esPedidoLimpiable() (borrado del pedido).
    esCuentaSaldada(cuenta, pedido) {
      // Mismos roles que la ruta cerrar-saldada (1, 2, 8): el Mesero no cierra
      // cuentas, así que no se le muestra un botón que siempre daría 403.
      if (![1, 2, 8].includes(Number(this.$store.state.user?.IdRol))) return false;
      return Number(pedido.EstadoPedido) === 1
        && Number(cuenta.Estado) === 0
        && Number(cuenta.Total) > 0.01
        && Number(cuenta.SaldoPendienteCuenta) <= 0.01;
    },
    abrirModalCerrarSaldada(cuenta, pedido) {
      this.cuentaASaldar = { ...cuenta, _idPedido: pedido.IdPedido };
      this.mostrarModalSaldada = true;
    },
    cerrarModalSaldada() {
      if (this.cerrandoSaldada !== null) return;
      this.mostrarModalSaldada = false;
      this.cuentaASaldar = null;
    },
    async confirmarCerrarSaldada() {
      if (!this.cuentaASaldar) return;
      const idCuenta = this.cuentaASaldar.IdCuentaPedido;
      try {
        this.cerrandoSaldada = idCuenta;
        await axios.put(`${API_URL}/cuentas-restaurante/${idCuenta}/cerrar-saldada`);
        this.mostrarAlerta(`Cuenta #${idCuenta} cerrada correctamente`, 'success');
        this.mostrarModalSaldada = false;
        this.cuentaASaldar = null;
        await this.cargarCuentas();
      } catch (err) {
        this.mostrarAlerta(err.response?.data?.message || 'Error al cerrar la cuenta', 'danger');
      } finally {
        this.cerrandoSaldada = null;
      }
    },
    esPedidoLimpiable(pedido) {
      return Number(pedido.EstadoPedido) === 1
        && pedido.cuentas.length > 0
        && pedido.cuentas.every(c =>
             Number(c.Total) <= 0.01 && Number(c.SaldoPendienteCuenta) <= 0.01
             && ![1, 2].includes(Number(c.Estado)));
    },
    abrirModalLimpiar(pedido) {
      this.pedidoALimpiar = pedido;
      this.mostrarModalLimpiar = true;
    },
    cerrarModalLimpiar() {
      if (this.limpiando !== null) return;
      this.mostrarModalLimpiar = false;
      this.pedidoALimpiar = null;
    },
    async confirmarLimpiarPedido() {
      if (!this.pedidoALimpiar) return;
      const idPedido = this.pedidoALimpiar.IdPedido;
      try {
        this.limpiando = idPedido;
        await axios.delete(`${API_URL}/pedidos/${idPedido}/limpiar-vacio`);
        this.mostrarAlerta(`Pedido #${idPedido} eliminado correctamente`, 'success');
        this.mostrarModalLimpiar = false;
        this.pedidoALimpiar = null;
        await this.cargarCuentas();
      } catch (err) {
        this.mostrarAlerta(err.response?.data?.message || 'Error al eliminar el pedido', 'danger');
      } finally {
        this.limpiando = null;
      }
    },
  },
  mounted() {
    this.cargarCuentas()
    this.iniciarPolling()
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.cargarCuentas()
    })
  },
  beforeUnmount() {
    this.detenerPolling()
  },
}
</script>

<style scoped>
.mesa-card {
  background-color: #fff;
  transition: transform 0.2s ease;
}
.mesa-card:hover {
  transform: translateY(-2px);
}
.mesa-header h6 {
  font-size: 0.95rem;
}
.mesa-body {
  background-color: #fafafa;
  border-top: 1px solid #ddd;
}
.badge {
  font-size: 0.75rem;
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  max-height: 0;
  overflow: hidden;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  max-height: 500px;
}

/* Modal de Habitación */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease;
}
.modal-content-custom {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0,0,0,0.3);
  animation: slideDown 0.3s ease;
}
.modal-header-custom {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
}
.modal-header-custom h5 {
  margin: 0;
  font-size: 1.1rem;
}
.btn-close-custom {
  background: transparent;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  line-height: 1;
}
.modal-body-custom {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}
.modal-footer-custom {
  padding: 1rem 1.5rem;
  border-top: 1px solid #ddd;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
.habitaciones-lista {
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 0.5rem;
}
.habitacion-item {
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f9f9f9;
}
.habitacion-item:hover {
  background: #e8f4f8;
  border-color: #0dcaf0;
}
.habitacion-item.selected {
  background: #d1ecf1;
  border-color: #0dcaf0;
  border-width: 2px;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes slideDown {
  from { transform: translateY(-20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

</style>
